import { Button } from '@uhg-abyss/web/ui/Button';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { I18nProvider } from '@uhg-abyss/web/ui/I18nProvider';
import find from 'lodash/find';
import React, { useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { CustomDisclaimerBanner } from '../../../common/CustomDisclaimerBanner/CustomDisclaimerBanner';
import { useAdobePageTrackEvent } from '../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useCoverageType } from '../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { CarePathsResponse } from '../../../models/CarePaths';
import { ProviderDetails } from '../../../models/ProviderDetails';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { suppressCostEstimateFromCostTab } from '../../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import {
  getNetworkStatus,
  getSelectedLocationIndex,
} from '../../../utils/providerDetails.utils';
import { getCurrentMember } from '../../../utils/user.utils';
import {
  Constants,
  CostEstimatesNotificationPolicy,
  ReverseCoverageTypesCodes,
} from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { convertProviderTypeToAdobeType } from '../../Utils/adobeTrackUtils/adobeTrackUtils';
import { CarePaths } from './CarePaths/CarePaths';
import {
  CommonCostProceduresHeader,
  CommonCostProceduresTextSection,
  CommonProceduresAndCostContainer,
  CostEstimateNotificationAlert,
} from './Cost.styled';
import { usePortalCostEstimate } from './costUtils';
import { InNetworkSection } from './InNetworkSection';

type Props = {
  carePathsData: CarePathsResponse;
  providerDetails: ProviderDetails;
  carePathPageNum: number;
  setCarePathPageNum: (num: number) => void;
  setCarePathStepCategory: (category: string) => void;
  tabTitle: string;
};

export const Cost = ({
  carePathsData,
  providerDetails,
  carePathPageNum,
  setCarePathPageNum,
  setCarePathStepCategory,
  tabTitle,
}: Props) => {
  const coverageType = useCoverageType();
  const currentMember = getCurrentMember();
  const [carePathDataAvailable, setCarePathDataAvailable] = useState(false);

  useEffect(() => {
    if (carePathsData && carePathsData?.carePathSteps) {
      setCarePathDataAvailable(carePathsData?.carePathSteps?.length > 0);
    } else {
      setCarePathDataAvailable(false);
    }
  }, [carePathsData]);
  const data = useLagoon('ui-messaging')();
  const customDisclaimerData = useLagoon('custom-disclaimers')();
  const suppressCostEstimateSection = suppressCostEstimateFromCostTab(
    currentMember?.policyNumber
  );

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: tabTitle,
    sitesectionLevel1: Constants.ADOBE_TRACKING.DETAILS_SITESECTION1,
    sitesectionLevel2: `${convertProviderTypeToAdobeType(
      providerDetails.providerType
    )} details`,
  });

  useEffect(() => {
    if (providerDetails.providerName) adobePageTrackEvent();
  }, [providerDetails.providerName]);

  const costEstimateText = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS.COST_TAB_COST_ESTIMATES_TEXT,
  });
  const costEstimateUpdatedText = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS.COST_TAB_COST_ESTIMATES_UPDATED_TEXT,
  });
  const costEstimateTextNotAvailable = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS
      .COST_TAB_COST_ESTIMATES_NOT_AVAILABLE_TEXT,
  });
  const costSuppressText = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS.COST_TAB_COST_SUPPRESS_TEXT,
  });
  const costEstimateHeading = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS.COST_TAB_COST_ESTIMATES_HEADING,
  });
  const costEstimateNotAvailableHeading = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS
      .COST_TAB_COST_ESTIMATES_NOT_AVAILABLE_HEADING,
  });
  const costSuppressHeading = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS.COST_TAB_COST_SUPPRESS_HEADING,
  });
  const costEstimateNotification = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS.COST_TAB_COST_ESTIMATES_NOTIFICATION,
  });
  const [
    enableCostEstimateNotification,
    enableRemoveEstimatedCostText,
    enableSupressCostIndicator,
    enableCEX,
    enableCostEstimateInCurrentTab,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COST_ESTIMATES_NOTIFICATION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_REMOVE_ESTIMATED_COST_TEXT,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SUPRESS_COST_INDICATOR,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CEX_GET_FULL_COST_ESTIMATE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COST_ESTIMATE_IN_CURRENT_TAB,
  ]);

  const locationsDetails = providerDetails?.providerLocations;
  const [selectedId] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    ''
  );
  const index = getSelectedLocationIndex(locationsDetails, selectedId);
  const selectedLocation = locationsDetails?.[index];
  const networkStatus = getNetworkStatus(selectedLocation?.network?.status);

  const { title, message } = costEstimateNotification;
  const [showNotification, setShowNotification] = useState(true);

  const getCostEstimateTextToShow = carePathDataAvailable
    ? enableRemoveEstimatedCostText
      ? costEstimateUpdatedText?.message
      : costEstimateText?.message
    : costEstimateTextNotAvailable?.message;

  const getCustomDisclaimerDataForCost = () => {
    const costDisclaimerData = customDisclaimerData?.filter(
      (data) =>
        data?.dnt_pageName ===
        Constants.CUSTOM_DISCALIMER_BANNER_PAGE_NAME.PROVIDER_DETAILS_COST_TAB
    );
    return costDisclaimerData?.filter(
      (data) =>
        data?.active &&
        data?.policyId
          ?.split(',')
          ?.map((id) => id.trim())
          ?.includes(currentMember?.policyNumber) &&
        data?.reciprocityId
          ?.split(',')
          ?.map((id) => id.trim())
          ?.includes(currentMember?.reciprocityId)
    );
  };

  const customDisclaimerDataForCost = getCustomDisclaimerDataForCost();
  const isGEHAV2 = shouldUseGehaV2Styling();
  const additonalCss = isGEHAV2 ? '$core.color.brand.100' : '#4B4D4F';

  const { locale } = useChipStore(
    useShallow((state: ChipState) => ({
      locale: state.locale,
    }))
  );

  const { handleViewCostEstimate } = usePortalCostEstimate(
    enableCEX,
    enableCostEstimateInCurrentTab,
    locale
  );

  const getCostEstimatedTitleAndMessage = (title: any, message: any) => (
    <>
      <Heading
        css={CommonCostProceduresHeader}
        data-auto-testid="cost-tab-cost-estimate-heading"
        data-testid="cost-tab-cost-estimate-heading"
        level={2}
        size="sm"
      >
        {title}
      </Heading>
      <CommonCostProceduresTextSection cssProps={{ isGEHAV2 }}>
        <Flex
          css={{
            '.&abyss-flex-root': {
              color: '$core.color.neutral.80',
              fontSize: '18px',
              '@media (max-width: 464px)': {
                fontSize: '16px',
                paddingTop: '12px',
              },
            },
            paddingBottom: '8px',
          }}
          data-testid="cost-tab-cost-estimate-text"
        >
          <div
            dangerouslySetInnerHTML={{
              __html: message,
            }}
            style={{
              color: additonalCss,
              ...(enableSupressCostIndicator
                ? { paddingTop: '8px', paddingBottom: '8px' }
                : {}),
            }}
          />
        </Flex>
      </CommonCostProceduresTextSection>
    </>
  );

  return (
    <React.Fragment>
      <CustomDisclaimerBanner data={customDisclaimerDataForCost} />
      {showNotification &&
      message &&
      enableCostEstimateNotification &&
      CostEstimatesNotificationPolicy.includes(currentMember?.policyNumber) &&
      coverageType === ReverseCoverageTypesCodes.MEDICAL ? (
        <CommonProceduresAndCostContainer>
          <I18nProvider translations={{ close: 'Close message' }}>
            <CostEstimateNotificationAlert
              css={costEstimateNotification}
              data-testid="cost-estimate-notification-alert"
              isVisible={showNotification}
              onClose={() => {
                setShowNotification(false);
              }}
              status="info"
              title={title}
            >
              {message}
            </CostEstimateNotificationAlert>
          </I18nProvider>
        </CommonProceduresAndCostContainer>
      ) : null}
      <InNetworkSection inNetwork={networkStatus} />
      <CommonProceduresAndCostContainer>
        {enableSupressCostIndicator ? (
          <>
            {getCostEstimatedTitleAndMessage(
              costSuppressHeading?.title,
              costSuppressText?.message
            )}
            <Button
              data-testid="get-cost-estimate-button"
              onClick={handleViewCostEstimate}
              variant="outline"
            >
              {costSuppressHeading?.message}
            </Button>
          </>
        ) : (
          <>
            {(!suppressCostEstimateSection || !carePathDataAvailable) && (
              <>
                {getCostEstimatedTitleAndMessage(
                  carePathDataAvailable
                    ? costEstimateHeading?.message
                    : costEstimateNotAvailableHeading?.message,
                  getCostEstimateTextToShow
                )}
                {carePathDataAvailable && (
                  <Divider
                    color="#E5E5E6"
                    margin="16px"
                    orientation="horizontal"
                  />
                )}
              </>
            )}
            <CarePaths
              carePathPageNumber={carePathPageNum}
              carePaths={carePathsData}
              selectedLocation={selectedLocation}
              setCarePathDataAvailable={setCarePathDataAvailable}
              setCarePathPageNum={setCarePathPageNum}
              setCarePathStepCategory={setCarePathStepCategory}
              suppressCostEstimateSection={suppressCostEstimateSection}
            />
          </>
        )}
      </CommonProceduresAndCostContainer>
    </React.Fragment>
  );
};
